<template>
  <div>
    <div v-if="ready && authenticated">
      <slot :session="session" :logout="logout"></slot>
    </div>

    <section v-else class="ticket-access-shell">
      <div class="ticket-access-card">
        <div class="ticket-access-scan" aria-hidden="true">
          <span class="ticket-access-scan__beam"></span>
          <span class="ticket-access-scan__pulse"></span>
        </div>

        <div class="ticket-access-copy">
          <span class="ticket-access-kicker">Acceso por codigo</span>
          <h2>{{ title }}</h2>
          <p>{{ subtitle }}</p>
        </div>

        <div class="ticket-access-panel">
          <label class="ticket-access-field">
            <span>Correo</span>
            <input v-model.trim="email" class="form-control checkout-input workspace-input" type="email" placeholder="tu@correo.com" :disabled="sending || verifying || stage === 'otp'" />
          </label>

          <label v-if="stage === 'otp'" class="ticket-access-field">
            <span>Codigo one-time</span>
            <input v-model.trim="otp" class="form-control checkout-input workspace-input" type="text" inputmode="numeric" maxlength="6" placeholder="Ingresa el codigo" :disabled="verifying" />
          </label>

          <p v-if="message" class="ticket-access-message">{{ message }}</p>
          <p v-if="error" class="ticket-access-error">{{ error }}</p>

          <div class="ticket-access-actions">
            <button v-if="stage === 'email'" class="ticket-access-button ticket-access-button--primary liquid-page-button" type="button" :disabled="sending || !validEmail" @click="sendCode">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ sending ? 'Enviando...' : 'Enviar codigo' }}</span>
                <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
              </span>
            </button>

            <button v-else class="ticket-access-button ticket-access-button--primary liquid-page-button" type="button" :disabled="verifying || otp.length < 4" @click="validateCode">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">{{ verifying ? 'Validando...' : 'Entrar' }}</span>
                <span class="liquid-page-button__icon"><i class="fas fa-arrow-right"></i></span>
              </span>
            </button>

            <button v-if="stage === 'otp'" class="ticket-access-button liquid-page-button" type="button" :disabled="sending" @click="resendCode">
              <span class="liquid-page-button__glow"></span>
              <span class="liquid-page-button__ring"></span>
              <span class="liquid-page-button__inner">
                <span class="liquid-page-button__copy">Reenviar codigo</span>
                <span class="liquid-page-button__icon"><i class="fas fa-redo"></i></span>
              </span>
            </button>

            <button v-if="stage === 'otp'" class="ticket-access-link" type="button" @click="backToEmail">
              Cambiar correo
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { apiService, apiServiceAuth } from '@/common/api.services'

const TOKEN_KEY = 'ticket_access_token'
const REFRESH_KEY = 'ticket_access_refresh_token'
const EMAIL_KEY = 'ticket_access_email'
const USER_ID_KEY = 'ticket_access_user_id'

export default {
  name: 'TicketAccessGate',

  props: {
    title: {
      type: String,
      default: 'Revisa tus entradas con un codigo por correo',
    },
    subtitle: {
      type: String,
      default: 'Ingresa tu correo, te enviamos un codigo one-time y con eso abres tus entradas o tu wallet sin password.',
    },
    prefillEmail: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      ready: false,
      authenticated: false,
      stage: 'email',
      email: this.prefillEmail || window.localStorage.getItem(EMAIL_KEY) || '',
      otp: '',
      sending: false,
      verifying: false,
      error: '',
      message: '',
      session: null,
    }
  },

  computed: {
    validEmail() {
      return /.+@.+\..+/.test(this.email)
    },
  },

  mounted() {
    this.bootstrapSession()
  },

  methods: {
    bootstrapSession() {
      const token = window.localStorage.getItem(TOKEN_KEY)
      if (!token) {
        this.ready = true
        return
      }

      this.fetchSession(token)
    },

    fetchSession(token) {
      apiServiceAuth('/api/user/ticket-session/', 'GET', undefined, token).then((data) => {
        if (!data || !data.id) {
          this.clearStoredAccess()
          this.ready = true
          return
        }

        this.session = data
        this.authenticated = true
        this.stage = 'otp'
        this.email = data.email || this.email
        this.ready = true
        this.$emit('authenticated', data)
      }).catch(() => {
        this.clearStoredAccess()
        this.ready = true
      })
    },

    clearStoredAccess() {
      window.localStorage.removeItem(TOKEN_KEY)
      window.localStorage.removeItem(REFRESH_KEY)
      window.localStorage.removeItem(EMAIL_KEY)
      window.localStorage.removeItem(USER_ID_KEY)
    },

    sendCode() {
      if (!this.validEmail) {
        this.error = 'Ingresa un correo valido.'
        return
      }

      this.sending = true
      this.error = ''
      this.message = ''

      apiService('/api/login-with-otp/', 'POST', { email: this.email.toLowerCase() }).then((data) => {
        if (!data || data.error || data.err) {
          this.error = (data && (data.error || data.err)) || 'No encontramos una cuenta con ese correo.'
          return
        }

        this.stage = 'otp'
        this.message = 'Te enviamos un codigo a tu correo. Revisalo y pegalo aqui.'
        window.localStorage.setItem(EMAIL_KEY, this.email.toLowerCase())
      }).finally(() => {
        this.sending = false
      })
    },

    resendCode() {
      this.sendCode()
    },

    validateCode() {
      this.verifying = true
      this.error = ''
      this.message = ''

      apiService('/api/validate-otp/', 'POST', {
        email: this.email.toLowerCase(),
        otp: this.otp,
      }).then((data) => {
        const accessToken = data && (data.access || data.token || '')
        if (!data || data.error || !accessToken) {
          this.error = data && data.error ? data.error : 'No pudimos validar el codigo.'
          return
        }

        window.localStorage.setItem(TOKEN_KEY, accessToken)
        if (data.refresh) {
          window.localStorage.setItem(REFRESH_KEY, data.refresh)
        }
        window.localStorage.setItem(EMAIL_KEY, this.email.toLowerCase())
        if (data.user && data.user.id) {
          window.localStorage.setItem(USER_ID_KEY, data.user.id)
        }

        this.fetchSession(accessToken)
      }).finally(() => {
        this.verifying = false
      })
    },

    backToEmail() {
      this.stage = 'email'
      this.otp = ''
      this.error = ''
      this.message = ''
    },

    logout() {
      this.clearStoredAccess()
      this.authenticated = false
      this.session = null
      this.otp = ''
      this.stage = 'email'
      this.message = ''
      this.$emit('signed-out')
    },
  },
}
</script>

<style scoped>
.ticket-access-shell {
  padding: 28px 0;
}

.ticket-access-card {
  position: relative;
  overflow: hidden;
  max-width: 860px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  gap: 18px;
  padding: 22px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(214, 221, 232, 0.08), transparent 28%),
    radial-gradient(circle at top right, rgba(98, 76, 144, 0.18), transparent 24%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.01)),
    rgba(9, 10, 15, 0.96);
  border: 1px solid rgba(201, 211, 231, 0.1);
  box-shadow: 0 26px 58px rgba(0, 0, 0, 0.28);
}

.ticket-access-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 16% 20%, rgba(161, 194, 255, 0.1), transparent 24%),
    radial-gradient(circle at 84% 74%, rgba(178, 132, 255, 0.1), transparent 28%);
  opacity: 0.62;
  animation: ticket-access-breathe 7s ease-in-out infinite alternate;
}

.ticket-access-scan {
  position: absolute;
  z-index: 0;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.ticket-access-scan__beam {
  position: absolute;
  top: -18%;
  bottom: -18%;
  left: -24%;
  width: 22%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(159, 203, 255, 0.05) 24%,
    rgba(196, 224, 255, 0.64) 50%,
    rgba(187, 147, 255, 0.16) 74%,
    transparent
  );
  filter: blur(9px);
  opacity: 0.7;
  transform: skewX(-12deg);
  animation: ticket-access-scan 6.8s cubic-bezier(0.32, 0.02, 0.28, 1) infinite;
}

.ticket-access-scan__pulse {
  position: absolute;
  top: 18%;
  right: 12%;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #d8eaff;
  box-shadow: 0 0 0 5px rgba(179, 211, 255, 0.08), 0 0 24px 8px rgba(151, 185, 255, 0.46);
  opacity: 0;
  animation: ticket-access-pulse 6.8s ease-in-out infinite;
}

.ticket-access-copy,
.ticket-access-panel {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 12px;
}

.ticket-access-kicker {
  color: #cfd8ea;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.ticket-access-copy h2 {
  margin: 0;
  color: #fff;
  font-size: clamp(2rem, 3vw, 2.6rem);
  line-height: 1.02;
  font-family: 'Syne', sans-serif;
  font-weight: 600;
  letter-spacing: -0.045em;
}

.ticket-access-copy p,
.ticket-access-message,
.ticket-access-error {
  margin: 0;
  line-height: 1.7;
  font-family: 'Manrope', sans-serif;
}

.ticket-access-copy p,
.ticket-access-message {
  color: rgba(194, 204, 223, 0.84);
}

.ticket-access-error {
  color: #ffb7b7;
}

.ticket-access-field {
  display: grid;
  gap: 8px;
  font-family: 'Space Grotesk', sans-serif;
}

.ticket-access-field span {
  color: rgba(210, 219, 234, 0.86);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.ticket-access-field input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid rgba(196, 206, 228, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #eef3fb;
  padding: 0 18px;
}

.ticket-access-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.ticket-access-button,
.ticket-access-link {
  min-height: 48px;
  border-radius: 999px;
  border: 1px solid rgba(196, 206, 228, 0.12);
  padding: 0 18px;
  background: rgba(255, 255, 255, 0.03);
  color: #eef3fb;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
}

.ticket-access-button--primary {
  background: linear-gradient(135deg, rgba(101, 121, 166, 0.98), rgba(94, 73, 146, 0.98));
}

.ticket-access-button:disabled {
  opacity: 0.45;
}

@keyframes ticket-access-scan {
  0%,
  12% {
    transform: translateX(0) skewX(-12deg);
    opacity: 0;
  }

  22% {
    opacity: 0.7;
  }

  58% {
    opacity: 0.58;
  }

  78%,
  100% {
    transform: translateX(610%) skewX(-12deg);
    opacity: 0;
  }
}

@keyframes ticket-access-pulse {
  0%,
  48%,
  100% {
    opacity: 0;
    transform: scale(0.8);
  }

  58% {
    opacity: 1;
    transform: scale(1);
  }

  68% {
    opacity: 0.15;
    transform: scale(1.4);
  }
}

@keyframes ticket-access-breathe {
  from { opacity: 0.46; }
  to { opacity: 0.82; }
}

@media (prefers-reduced-motion: reduce) {
  .ticket-access-card::before,
  .ticket-access-scan__beam,
  .ticket-access-scan__pulse {
    animation: none;
  }

  .ticket-access-scan__beam {
    opacity: 0.14;
  }
}

@media (max-width: 860px) {
  .ticket-access-card {
    grid-template-columns: 1fr;
  }
}
</style>
